import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Wallet, QrCode, Mail, CreditCard, Sparkles } from "lucide-react";
import SendCreditsCard from "@/components/SendCreditsCard";
import RecentTransfers from "@/components/RecentTransfers";
import logger from "@/lib/logger";

const PACKAGE_LABELS = {
    half_day: "Half Day · 1 credit",
    full_day: "Full Day · 2 credits",
    credits_4: "4 Credit Pack",
    credits_8: "8 Credit Pack",
    credits_12: "12 Credit Pack",
    credits_16: "Routine Pack · 16 credits",
};

// Long-form pitch shown when a customer selects a package.
const PACKAGE_PITCH = {
    half_day: "A single 4-hour session. Best for one-off catch-ups, focused study blocks, or visiting clients.",
    full_day: "A full 8-hour day. Best for client-heavy days, deep work sprints, or full-day study marathons.",
    credits_4: "Two full days, or four half-days. Great for a busy week or the run-up to an exam.",
    credits_8: "Four full days of office time. Perfect for project-based work and weekly study routines.",
    credits_12: "Six full days of office time. The sweet spot for consultants with multiple recurring engagements.",
    credits_16: "Eight full days — 2 full-days per week for a month. Built for students preparing for finals, professionals delivering a 4-week engagement, and anyone who wants a predictable, repeating routine. Note: bookings remain blocked on weekends and Barbados public holidays.",
};

export default function BuyCredits() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [packages, setPackages] = useState([]);
    const [credits, setCredits] = useState(null);
    const [purchases, setPurchases] = useState([]);
    const [selected, setSelected] = useState("half_day");
    const [qty, setQty] = useState(1);
    const [active, setActive] = useState(null); // pending purchase awaiting payment
    const [reference, setReference] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [stripeLoading, setStripeLoading] = useState(false);
    const [requestMethodOpen, setRequestMethodOpen] = useState(false);
    const [transfersRefresh, setTransfersRefresh] = useState(0);

    const isReferenceValid = /^[a-zA-Z0-9]{17,}$/.test(reference.trim());

    const load = async () => {
        const [p, c, pur] = await Promise.all([
            api.get("/credits/packages"),
            api.get("/credits/me"),
            api.get("/purchases/me"),
        ]);
        setPackages(p.data);
        setCredits(c.data);
        setPurchases(pur.data);
    };

    useEffect(() => {
        load();
    }, []);

    // Handle Stripe redirect-back: poll status for up to ~30s on a successful return.
    useEffect(() => {
        const sessionId = searchParams.get("session_id");
        const cancelled = searchParams.get("cancelled");
        if (cancelled) {
            toast.message("Card payment cancelled", { description: "Your order is still pending — try again or use PayPal." });
            const next = new URLSearchParams(searchParams);
            next.delete("cancelled");
            setSearchParams(next, { replace: true });
            return;
        }
        if (!sessionId) return;
        let attempts = 0;
        const maxAttempts = 10;
        const poll = async () => {
            attempts += 1;
            try {
                const r = await api.get(`/purchases/stripe/status/${sessionId}`);
                if (r.data.payment_status === "paid") {
                    toast.success("Payment received", { description: "Credits have been added to your account." });
                    const next = new URLSearchParams(searchParams);
                    next.delete("session_id");
                    setSearchParams(next, { replace: true });
                    await load();
                    return;
                }
                if (r.data.status === "expired") {
                    toast.error("Stripe session expired", { description: "Please try again." });
                    const next = new URLSearchParams(searchParams);
                    next.delete("session_id");
                    setSearchParams(next, { replace: true });
                    return;
                }
            } catch (e) {
                // Network blip while polling Stripe status — log + retry up to maxAttempts.
                logger.debug("StripePoll", "retry", attempts, e?.message || e);
            }
            if (attempts < maxAttempts) {
                setTimeout(poll, 3000);
            } else {
                toast.message("Still processing", { description: "We'll keep checking. Refresh if your balance doesn't update in a minute." });
            }
        };
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const createPurchase = async () => {
        try {
            const r = await api.post("/purchases/create", { package: selected, quantity: qty });
            setActive(r.data);
            await load();
        } catch (e) {
            toast.error("Could not create order", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    const startStripeCheckout = async () => {
        if (!active) return;
        setStripeLoading(true);
        try {
            const r = await api.post("/purchases/stripe/checkout", {
                purchase_id: active.id,
                origin: window.location.origin,
            });
            if (r.data?.url) {
                window.location.href = r.data.url;
            }
        } catch (e) {
            toast.error("Could not start card checkout", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setStripeLoading(false);
        }
    };

    const submitPayment = async () => {
        if (!active || !isReferenceValid) return;
        setSubmitting(true);
        try {
            await api.post("/purchases/submit-payment", { purchase_id: active.id, paypal_reference: reference.trim() });
            toast.success("Payment submitted", { description: "An admin will verify and credit your account." });
            setActive(null);
            setReference("");
            await load();
        } catch (e) {
            toast.error("Submit failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setSubmitting(false);
        }
    };

    const discardPurchase = async () => {
        if (!active) return;
        try {
            await api.delete(`/purchases/${active.id}`);
            toast.success("Order discarded");
            setActive(null);
            setReference("");
            await load();
        } catch (e) {
            toast.error("Could not discard", { description: formatApiError(e.response?.data?.detail) || e.message });
            // still clear local UI state so user isn't stuck
            setActive(null);
            setReference("");
            await load();
        }
    };

    const cancelPurchase = async (purchaseId) => {
        try {
            await api.post(`/purchases/${purchaseId}/cancel`);
            toast.success("Order cancelled");
            // If the active QR flow was for this purchase, clear it too
            if (active && active.id === purchaseId) {
                setActive(null);
                setReference("");
            }
            await load();
        } catch (e) {
            toast.error("Could not cancel", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    const pkg = packages.find((p) => p.key === selected);

    return (
        <div className="px-6 md:px-12 lg:px-16 py-10 max-w-6xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-2">Credits</p>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">Buy credits</h1>
                    <p className="text-base text-slate-600 mt-2">Pay via PayPal, Wire Transfer — credits are credited to your account once payment is confirmed.</p>
                </div>
                <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-md px-6 py-4">
                    <Wallet className="h-7 w-7 text-brand" strokeWidth={1.75} />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-slate-500">Balance</div>
                        <div className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tighter leading-none mt-1" data-testid="credits-balance">
                            {credits?.balance ?? 0}
                            <span className="ml-2 text-base sm:text-lg font-mono font-medium text-slate-500 tracking-normal align-middle">credits</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
                {/* Order */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6 md:p-8">
                    <h2 className="font-heading text-xl font-bold text-slate-900">Choose a package</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                        {packages.map((p) => {
                            const isFeature = p.key === "credits_16";
                            return (
                                <button
                                    key={p.key}
                                    onClick={() => setSelected(p.key)}
                                    data-testid={`pkg-${p.key}`}
                                    className={`relative text-left p-5 border rounded-md transition-all duration-200 ${
                                        selected === p.key
                                            ? "border-brand bg-brand/5 ring-1 ring-brand"
                                            : "border-slate-200 hover:border-slate-300"
                                    }`}
                                >
                                    {isFeature && (
                                        <div className="absolute -top-2 right-3 bg-brand text-white text-[10px] uppercase tracking-[0.16em] font-mono px-2 py-0.5 rounded-sm inline-flex items-center gap-1">
                                            <Sparkles className="h-3 w-3" strokeWidth={2} /> New
                                        </div>
                                    )}
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-slate-500">{p.label}</div>
                                    <div className="font-heading text-3xl font-bold text-slate-900 mt-2 tracking-tighter">${p.price_usd}</div>
                                    <div className="text-xs text-slate-500 mt-1">{p.credits} credit{p.credits !== 1 ? "s" : ""}</div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Pitch / write-up for the selected package */}
                    {PACKAGE_PITCH[selected] && (
                        <div className="mt-5 p-4 bg-slate-50 border border-slate-200 rounded-md">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-slate-500">About this pack</div>
                            <p className="text-sm text-slate-700 mt-1.5 leading-relaxed" data-testid={`pkg-pitch-${selected}`}>
                                {PACKAGE_PITCH[selected]}
                            </p>
                        </div>
                    )}

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                        <div className="sm:col-span-1">
                            <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Quantity</Label>
                            <Input
                                type="number"
                                min={1}
                                max={20}
                                value={qty}
                                onChange={(e) => setQty(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                                data-testid="pkg-qty-input"
                                className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                            />
                        </div>
                        <div className="sm:col-span-2 flex items-end justify-between gap-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500">Total</div>
                                <div className="font-heading text-3xl font-bold text-slate-900 tracking-tighter">${(pkg ? pkg.price_usd * qty : 0).toFixed(2)}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{pkg ? pkg.credits * qty : 0} credits</div>
                            </div>
                            <Button
                                onClick={createPurchase}
                                disabled={!!active}
                                data-testid="create-order-btn"
                                className="bg-brand hover:bg-brand-hover text-white rounded-md h-11 px-5"
                            >
                                {active ? "Order pending" : "Create order"}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* PayPal QR */}
                <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8">
                    <div className="flex items-center gap-2">
                        <QrCode className="h-5 w-5 text-slate-700" strokeWidth={1.75} />
                        <h2 className="font-heading text-xl font-bold text-slate-900">Pay with PayPal</h2>
                    </div>
                    {!active ? (
                        <p className="text-sm text-slate-500 mt-4">Create an order to reveal the PayPal QR and amount to send.</p>
                    ) : (
                        <div className="mt-5">
                            <div className="aspect-square bg-white border border-slate-200 rounded-md grid place-items-center p-3">
                                <img
                                    alt="PayPal QR — Allied Consulting"
                                    src={active.paypal_qr_url || "/assets/paypal-qr.jpg"}
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <div className="mt-5 space-y-1.5 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Send to</span>
                                    <span className="font-mono text-slate-900">{active.paypal_handle || "@alliedconsulting"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Amount</span>
                                    <span className="font-mono font-semibold text-slate-900">${active.amount_usd.toFixed(2)} USD</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Order ID</span>
                                    <span className="font-mono text-xs text-slate-700">{active.id.slice(0, 8)}…</span>
                                </div>
                            </div>
                            <div className="mt-5">
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">PayPal transaction reference</Label>
                                <Input
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    placeholder="e.g. 2KW83847LL123456N"
                                    data-testid="paypal-ref-input"
                                    className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                />
                                <p className="text-xs text-slate-500 mt-1.5">
                                    Found in your PayPal receipt email. Must be at least 17 alphanumeric characters.
                                    {reference.length > 0 && !isReferenceValid && (
                                        <span className="block text-red-600 mt-1" data-testid="paypal-ref-error">
                                            Reference must be at least 17 alphanumeric characters (letters/numbers only).
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="mt-4 flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={discardPurchase}
                                    data-testid="discard-order-btn"
                                    className="rounded-md flex-1"
                                >
                                    Discard
                                </Button>
                                <Button
                                    onClick={submitPayment}
                                    disabled={!isReferenceValid || submitting}
                                    data-testid="submit-payment-btn"
                                    className="bg-brand hover:bg-brand-hover text-white rounded-md flex-1"
                                >
                                    {submitting ? "Submitting…" : "Confirm Payment"}
                                </Button>
                            </div>
                            <div className="mt-4 relative">
                                <div className="absolute inset-x-0 top-1/2 h-px bg-slate-200" />
                                <div className="relative flex justify-center">
                                    <span className="bg-white px-3 text-[10px] uppercase tracking-[0.2em] font-mono text-slate-500">or pay by card</span>
                                </div>
                            </div>
                            <Button
                                onClick={startStripeCheckout}
                                disabled={stripeLoading}
                                data-testid="pay-with-card-btn"
                                variant="outline"
                                className="mt-3 w-full rounded-md h-11 border-slate-300 text-slate-900 hover:bg-slate-50"
                            >
                                <CreditCard className="h-4 w-4 mr-2" strokeWidth={1.75} />
                                {stripeLoading ? "Redirecting to Stripe…" : "Pay with credit/debit card"}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setRequestMethodOpen(true)}
                                data-testid="request-another-method-btn"
                                className="mt-3 w-full rounded-md h-11 border-slate-200 text-slate-700 hover:bg-slate-50"
                            >
                                <Mail className="h-4 w-4 mr-2" strokeWidth={1.75} />
                                Request Another Method
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Send credits to another member */}
            <div className="mt-10">
                <SendCreditsCard
                    balance={credits?.balance ?? 0}
                    onTransferred={() => {
                        load();
                        setTransfersRefresh((n) => n + 1);
                    }}
                />
                <RecentTransfers refreshKey={transfersRefresh} />
            </div>

            {/* Purchase history */}
            <div className="mt-10 bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                    <h2 className="font-heading text-lg font-bold text-slate-900">Purchase history</h2>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-left">
                        <tr>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Date</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Package</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Credits</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Amount</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Status</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {purchases.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No purchases yet.</td></tr>}
                        {purchases.map((p) => (
                            <tr key={p.id} className="border-b border-slate-100 last:border-0">
                                <td className="px-5 py-3 text-slate-700">{format(parseISO(p.created_at), "MMM d, yyyy")}</td>
                                <td className="px-5 py-3 text-slate-700">{PACKAGE_LABELS[p.package] || p.package} × {p.quantity}</td>
                                <td className="px-5 py-3 text-slate-700">{p.credits}</td>
                                <td className="px-5 py-3 font-mono text-slate-900">${p.amount_usd.toFixed(2)}</td>
                                <td className="px-5 py-3"><PurchaseBadge status={p.status} /></td>
                                <td className="px-5 py-3 text-right">
                                    {p.status === "pending_payment" ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => cancelPurchase(p.id)}
                                            data-testid={`cancel-purchase-${p.id}`}
                                            className="rounded-md h-8 px-3 text-xs border-slate-200 text-slate-700 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                                        >
                                            Cancel
                                        </Button>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={requestMethodOpen} onOpenChange={setRequestMethodOpen}>
                <DialogContent data-testid="request-method-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-slate-900">Request another payment method</DialogTitle>
                        <DialogDescription className="text-slate-600 pt-2">
                            Please send an email to us at{" "}
                            <a
                                href="mailto:alliedconsulting.global@gmail.com"
                                className="font-mono text-brand underline underline-offset-2"
                            >
                                alliedconsulting.global@gmail.com
                            </a>{" "}
                            with your desired date, time-slot and payment method, and we'll do our best to facilitate your request.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            onClick={() => setRequestMethodOpen(false)}
                            data-testid="request-method-close-btn"
                            className="bg-brand hover:bg-brand-hover text-white rounded-md w-full"
                        >
                            Got it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PurchaseBadge({ status }) {
    const map = {
        pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
        submitted: "bg-blue-50 text-blue-700 border-blue-200",
        approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
        rejected: "bg-red-50 text-red-700 border-red-200",
        refunded: "bg-slate-50 text-slate-700 border-slate-200",
        cancelled: "bg-slate-100 text-slate-600 border-slate-200",
    };
    return (
        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-mono border rounded-sm ${map[status] || "bg-slate-50 text-slate-700"}`}>
            {status.replace("_", " ")}
        </span>
    );
}
